// src/WorkflowScreen.jsx
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { AccessContext } from '../contexts/AccessProvider';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { useWorkflowCache } from '../contexts/WorkflowCacheContext';
import WorkflowView from '../components/WorkflowViewer';
import LoadingView from '../../shared/components/LoadingView';
import ErrorView from '../components/errors/ErrorView';
export default function WorkflowScreen() {
  const { task_id, access_key } = useParams();
  const navigate = useNavigate();
  
  const { assetMap, loading, error, refreshAll, fetchAndValidateAccess } =
    useContext(AccessContext);
  const { userProfile } = useContext(UserProfileContext);
  const { 
    ensureTaskSnapshot, 
    ensureAllFormDefinitions, 
    checkAllFormsPresent,
    clearTaskNamespace 
  } = useWorkflowCache();

  const asset = assetMap[access_key]?.data;

  const selectedTask = useMemo(() => {
    if (!asset?.tasks || task_id == null) return null;
    const tid = typeof task_id === 'string' ? Number(task_id) : task_id;
    return asset.tasks[tid] ?? asset.tasks[String(tid)] ?? asset.tasks[task_id];
  }, [asset, task_id]);

  /**
   * Where closing the task leaves the visitor.
   *
   * An anonymous User Workflow submitter gets the thank-you page: they have no
   * field engineer profile, no standing access to anything, and nothing to do
   * next — the whole visit was the one form.
   *
   * Everyone else goes back to the asset they just worked on. The access key is
   * still valid, usually for weeks, and the asset may carry other tasks
   * assigned to them. This used to be `/`, which renders "No Active Accesses"
   * once the task that made the asset active is closed — telling someone who
   * had just finished a job that they have nothing, while the same asset sat
   * under the adjacent tab.
   */
  const afterClosePath = useMemo(() => {
    if (!access_key) return '/';
    const isUserWorkflow = selectedTask?.workflow?.user_workflow === true;
    if (isUserWorkflow && !userProfile?.field_engineer_id) {
      return `/assets/access_key/${access_key}/thank-you`;
    }
    return `/assets/access_key/${access_key}`;
  }, [selectedTask, userProfile?.field_engineer_id, access_key]);

  const [workflow, setWorkflow] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [currentFormValid, setCurrentFormValid] = useState(true);
  const [accessBootstrapDone, setAccessBootstrapDone] = useState(false);
  const { setTopBarProps } = useOutletContext();

  useEffect(() => {
    if (!access_key) {
      setAccessBootstrapDone(true);
      return;
    }
    let cancelled = false;
    setAccessBootstrapDone(false);
    (async () => {
      try {
        await fetchAndValidateAccess(access_key);
      } finally {
        if (!cancelled) setAccessBootstrapDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access_key, fetchAndValidateAccess]);

  // Load workflow and setup cache
  useEffect(() => {
    if (asset && asset.tasks && selectedTask) {
      if (selectedTask.workflow && selectedTask.workflow.workflow_steps) {
        const workflowSteps = selectedTask.workflow.workflow_steps;
        setWorkflow(workflowSteps);

        // Ensure workflow snapshot is saved
        ensureTaskSnapshot({
          accessKey: access_key,
          taskId: task_id,
          workflow: workflowSteps,
        });

        // Only show Diagnostics button if asset has IoT integration
        const topBarConfig = {
          headerText: `${selectedTask.workflow.name} – ${asset.asset_name}`,
        };

        if (asset.has_iot_integration) {
          topBarConfig.rightIconClass = 'bi-graph-down-arrow';
          topBarConfig.rightButtonText = 'Diagnostics';
          topBarConfig.onRightIconClick = () => navigate('../../../advanced', { relative: 'path' });
        }

        setTopBarProps(topBarConfig);
      } else {
        console.error('Workflow not found for task_id:', task_id);
        setWorkflow(null);
      }
    } else {
      console.error('No tasks found in asset.');
      setWorkflow(null);
    }
  }, [
    asset,
    selectedTask,
    task_id,
    access_key,
    ensureTaskSnapshot,
    setTopBarProps,
    navigate,
  ]);

  // Load form definitions when workflow is available.
  // Do not list formLoading/formError in deps — toggling them after load would re-run this effect and reload in a loop (flicker).
  useEffect(() => {
    if (!workflow) return;

    let cancelled = false;

    const loadForms = async () => {
      setFormLoading(true);
      setFormError(null);

      try {
        const result = await ensureAllFormDefinitions({
          accessKey: access_key,
          taskId: task_id,
        });

        if (cancelled) return;
        if (!result.ok) {
          setFormError(result.error);
        }
      } catch (err) {
        if (!cancelled) setFormError(err);
      } finally {
        if (!cancelled) setFormLoading(false);
      }
    };

    loadForms();
    return () => {
      cancelled = true;
    };
  }, [workflow, access_key, task_id, ensureAllFormDefinitions]);

  const handleNavigate = (targetViewId) => {
    navigate(`../${targetViewId}`, { relative: 'path' });
  };

  const handleFormValidChange = (isValid) => {
    setCurrentFormValid(isValid);
  };

  const handleReload = async () => {
    setFormError(null);
    setFormLoading(true);
    
    try {
      const result = await ensureAllFormDefinitions({ 
        accessKey: access_key, 
        taskId: task_id 
      });
      
      if (!result.ok) {
        setFormError(result.error);
      }
    } catch (err) {
      setFormError(err);
    } finally {
      setFormLoading(false);
    }
  };

  const waitingForAsset = loading || !accessBootstrapDone;

  if (waitingForAsset) {
    return (
      <LoadingView
        title="Loading Workflow..."
        subtitle="Please wait while we load the workflow definition."
        fullViewport
      />
    );
  }

  if (error) {
    return (
      <ErrorView
        title="Error"
        message={`Failed to load workflow: ${error.message}`}
        onRetry={refreshAll}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  if (!asset) {
    return (
      <ErrorView
        title="Error"
        message={`No asset associated with access key ${access_key} found.`}
        onRetry={refreshAll}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  if (!workflow) {
    return (
      <ErrorView
        title="Error"
        message={`No workflow found for ${task_id}`}
        onRetry={refreshAll}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  if (formLoading) {
    return (
      <LoadingView
        title="Loading Forms..."
        subtitle="Please wait while we load the form definitions."
        fullViewport
      />
    );
  }

  if (formError) {
    return (
      <ErrorView
        title="Form Loading Error"
        message={`Failed to load form definitions: ${formError.message}`}
        onRetry={handleReload}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  // Check if all forms are present
  if (!checkAllFormsPresent({ accessKey: access_key, taskId: task_id })) {
    return (
      <ErrorView
        title="Missing Forms"
        message="Some form definitions could not be loaded. Please try refreshing."
        onRetry={handleReload}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  return (
    <WorkflowView
      workflow={workflow}
      preview_mode={false}
      task_id={task_id}
      accessKeyToken={access_key}
      onNavigate={handleNavigate}
      onFormValidChange={handleFormValidChange}
      closeTask={{
        taskId: task_id,
        accessKeyToken: access_key,
        afterClosePath,
        disabled: !currentFormValid,
        onTaskClosed: () =>
          clearTaskNamespace({ accessKey: access_key, taskId: task_id }),
      }}
    />
  );
}
